import { computed, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { Chats, Experiences, Filters, Search, Tools } from '@local/client-contracts';
import { observable, ManualPromise } from '@local/common';
import {
  generateId,
  getAssistantDescription,
  getAssistantTitle,
  isAgenticAssistant,
  isChatAssistant,
  isEmbed,
  isExtension,
  isGeneralAssistant,
} from '@local/common-web';
import { LogService, ServicesRpcService, WindowService } from '@shared/services';
import { RouterService } from '@shared/services/router.service';
import { Logger } from '@unleash-tech/js-logger';
import { isBoolean, isEmpty, uniq } from 'lodash';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  of,
  switchMap,
} from 'rxjs';
import { AvatarItemModel } from 'src/app/bar/models/avatar-item.model';
import { WorkspacesService } from 'src/app/bar/services';
import { ChatAssistantsService } from 'src/app/bar/services/chat-assistants.service';
import { FiltersService } from 'src/app/bar/services/filters.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { ChatsRpcInvoker } from 'src/app/bar/services/invokers/chats.rpc-invoker';
import { CHAT_PAGE_PATH, CHAT_PAGE_PATH_SESSION, SIDE_PANEL_PAGE_PATH, UNLEASH_AI_CHAT_PATH } from 'src/app/bar/utils/constants';
import { AnswerSearchItem } from '../../results';
import { AssistantChatData, ChatErrorState } from '../model';
import { ChatResourcesService } from './chat-resources.service';
import { AvatarListService } from 'src/app/bar/services/avatar-list.service';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ExperiencesInvokerService } from 'src/app/bar/services/experiences-invoker.service';
import { WORKSPACE_PERMISSIONS, WorkspacePermissionService } from 'src/app/bar/services/workspace-permissions.service';
import { AnswerResourceAgenticService } from 'src/app/bar/services/answer-resource-agentic';
import { content } from '../../settings/components/settings.content';

const QUESTION_DRAFT_QUERY = 'q-query';
const IS_GLOBAL_ASSISTANT = 'global';
const DEFAULT_SEARCH_EXPERIENCE_TITLE = 'Default Search Experience';

export const SELECTED_REGEX = /\[([\w\s]+)\]/;

export type AssistantDetails = { assistant?: Experiences.ExperienceItem; error?: ChatErrorState };

@Injectable({ providedIn: 'root' })
export class ChatsService {
  private readonly TEMP_CHAT_SESSION_STORAGE_KEY = 'temp_chat_session';
  readonly content = content['workspace-assistant'];
  private logger: Logger;
  private service: Chats.Service;
  private _sessions$ = new ReplaySubject<Chats.ChatSession[]>(1);
  private _currentSessionId$ = new BehaviorSubject<string>(null);
  private _currentAssistant$ = new BehaviorSubject<AssistantChatData>(null);
  private isEmbed = isEmbed();
  private localTempChat: Chats.Chat;
  private tempChatSession: Chats.Chat;
  private _assistantsForChat$ = new ReplaySubject<{ [key: string]: AssistantChatData }>(1);
  private chatSessionsPromise: { [key: string]: ManualPromise<boolean> } = {};
  private isExtension = isExtension();
  private sessions: Signal<Chats.ChatSession[]> = signal(null);
  private isOwnerOrAdmin: Signal<boolean> = signal(false);
  public assistantsForChat: Signal<{ [key: string]: AssistantChatData }> = signal(null);
  isUnleashAi = computed(() => this.workspacePermissionService.hasWorkspacePermission(WORKSPACE_PERMISSIONS.UNLEASH_AI));
  isUnleashAi$: Observable<boolean> = toObservable(this.isUnleashAi).pipe(distinctUntilChanged());
  private readonly hideAssistants: Signal<Set<string>> = computed(
    () => new Set(this.chatAssistantsService.hiddenAssistantsStorageValue()) || new Set<string>()
  );
  readonly pinnedAssistantsSet: Signal<Set<string>> = computed(() => new Set(this.pinnedAssistants().map((assistant) => assistant.id)));

  private readonly pinnedAssistants = computed(() => {
    const assistants = Object.values(this.assistantsForChat() || {});
    const pinnedAssistants = assistants.filter((assistant) => assistant.pinToChatPage) || [];
    return pinnedAssistants.sort((a, b) => {
      if (a.isDefault && !b.isDefault) {
        return -1;
      }
      if (!a.isDefault && b.isDefault) {
        return 1;
      }
      return a?.name?.localeCompare(b.name);
    });
  });

  private readonly interactionAssistants = computed(() => {
    return uniq(this.sessions()?.map((session) => session.assistantId))
      .map((id) => this.assistantsForChat()?.[id])
      .filter((assistant) => {
        if (!assistant) {
          return false;
        }
        return assistant.unleashAiId
          ? !this.hideAssistants().has(assistant.unleashAiId) && !this.pinnedAssistantsSet().has(undefined)
          : !this.hideAssistants().has(assistant.id) && !this.pinnedAssistantsSet().has(assistant.id);
      });
  });

  readonly interactionAssistantsSet: Signal<Set<string>> = computed(
    () => new Set(this.interactionAssistants().map((assistant) => assistant.id))
  );

  readonly pinnedAssistantsList = computed(() => [...this.pinnedAssistants(), ...this.interactionAssistants()]);

  @observable
  get assistantsForChat$(): Observable<{ [key: string]: AssistantChatData }> {
    return this._assistantsForChat$;
  }

  @observable
  get currentAssistant$(): Observable<AssistantChatData> {
    return this._currentAssistant$;
  }

  set currentAssistant(assistant: AssistantChatData) {
    this._currentAssistant$.next(assistant);
  }

  @observable
  get currentSessionId$(): Observable<string> {
    return this._currentSessionId$;
  }

  set currentSessionId(chadId: string) {
    this._currentSessionId$.next(chadId);
  }

  @observable
  get latestChatIdByCurrentAssistant$(): Observable<string> {
    return this.currentAssistant$.pipe(
      filter((currentAssistant) => !!currentAssistant),
      switchMap((currentAssistant) =>
        this.sessions$.pipe(
          map((sessions) => {
            const currentSessions = sessions.filter((session) => session.assistantId === currentAssistant?.id && !session.ignore);
            return currentSessions[0]?.id;
          })
        )
      )
    );
  }

  @observable
  get sessions$(): Observable<Chats.ChatSession[]> {
    return this._sessions$.asObservable();
  }

  constructor(
    services: ServicesRpcService,
    logger: LogService,
    private routerService: RouterService,
    private windowService: WindowService,
    private filtersService: FiltersService,
    private hubService: HubService,
    private chatResourcesService: ChatResourcesService,
    private workspaceService: WorkspacesService,
    private chatAssistantsService: ChatAssistantsService,
    private avatarListService: AvatarListService,
    private experiencesInvokerService: ExperiencesInvokerService,
    private workspacePermissionService: WorkspacePermissionService,
    private answerResourceAgenticService: AnswerResourceAgenticService
  ) {
    this.logger = logger.scope('ChatsService');
    this.service = services.invokeWith(ChatsRpcInvoker, 'chats');
    this.service.sessions$.subscribe((sessions) => {
      this._sessions$.next(sessions);
    });
    this.initAssistantsData();
    this.assistantsForChat = toSignal(this.assistantsForChat$);
    this.sessions = toSignal(this.sessions$);
    this.isOwnerOrAdmin = toSignal(this.workspaceService.ownerOrAdmin$);
  }

  createAssistantChatData(item: Experiences.ExperienceItem): AssistantChatData {
    if (!item || !isChatAssistant(item)) {
      return;
    }

    let knowledgeType, pinToChatPage, conversationStarterMessages, displayOnChatPage, allowFileUpload, allowWebAccess;
    if (isAgenticAssistant(item)) {
      pinToChatPage = item?.settings?.pinToChatPage;
      conversationStarterMessages = item?.settings?.conversationStarterMessages;
      displayOnChatPage = item?.settings?.displayOnChatPage;
      const toolResults = this.processTools(item, item.settings as Experiences.ExperienceAgenticSettings);
      allowFileUpload = toolResults.allowFileUpload;
      allowWebAccess = toolResults.allowWebAccess;
    } else if (isGeneralAssistant(item)) {
      knowledgeType = item?.settings?.general?.knowledgeType;
      pinToChatPage = item?.settings?.general?.pinToChatPage;
      conversationStarterMessages = item?.settings?.general?.conversationStarterMessages;
      displayOnChatPage = item?.settings?.general?.displayOnChatPage;
      allowFileUpload = item.settings?.experienceTools?.fileSearch?.enabled;
      allowWebAccess = item.settings?.experienceTools?.webSearch?.enabled;
    }

    return {
      experienceType: item.experienceType,
      id: item.id,
      emoji: item.settings?.emoji,
      name: getAssistantTitle(item),
      createdByInfo: this.getCreatedByInfo(item),
      description: getAssistantDescription(item),
      originalDescription: item.settings?.description,
      fullAccess: ['creator', 'editor', 'sourceOwner'].includes(item?.permissionRole),
      icon: !item.settings?.emoji ? { type: 'font', value: 'icon-assistant' } : null,
      knowledgeType,
      iconBlobId: item.settings?.iconBlobId,
      model: item.settings?.model,
      conversationStarterMessages,
      state: this.chatAssistantsService.isValidAssistantForChat(item) ? 'enabled' : 'disabled',
      allowFileUpload: allowFileUpload,
      allowWebAccess: allowWebAccess,
      createBy: item.createdBy,
      createdAt: item.createdAt,
      modifiedAt: item.modifiedAt,
      pinToChatPage,
      displayOnChatPage,
    };
  }

  private processTools(
    item: Experiences.ExperienceItem,
    itemSettings: Experiences.ExperienceAgenticSettings
  ): { allowFileUpload: boolean; allowWebAccess: boolean } {
    const tools = item.tools?.filter((tool) => tool.type === Tools.ToolType.FileSearch || tool.type === Tools.ToolType.Web) || [];

    if (tools.length > 0 && itemSettings?.tools) {
      const enabledTools = tools.filter((tool) => {
        const toolSetting = itemSettings.tools.find((s) => s.toolId === tool.id);
        return toolSetting?.enabled;
      });

      const allowFileUpload = enabledTools.some((tool) => tool.type === Tools.ToolType.FileSearch);
      const allowWebAccess = enabledTools.some((tool) => tool.type === Tools.ToolType.Web);

      return { allowFileUpload, allowWebAccess };
    }

    return { allowFileUpload: false, allowWebAccess: false };
  }

  private initAssistantsData() {
    combineLatest([this.chatAssistantsService.assistantForChat$, this.isUnleashAi$]).subscribe(
      async ([items, isUnleashAi]) => {
        try {
          const assistants: { [key: string]: AssistantChatData } = {};
          items.forEach((item) => {
            assistants[item.id] = this.createAssistantChatData(item);
          });
          if (isUnleashAi) {
            const defaultAssistant = await this.getWorkspaceAssistantData();
            assistants[defaultAssistant.id] = defaultAssistant;
          }
          this._assistantsForChat$.next(assistants);
        } catch (error) {
          this.logger.error('Failed to initialize assistants for chat', error);
        }
      },
      async (error) => {
        this.logger.error('Failed to load assistants for chat', error);
        if (this.isUnleashAi) {
          const defaultAssistant = await this.getWorkspaceAssistantData();
          this._assistantsForChat$.next({ [defaultAssistant.id]: defaultAssistant });
        } else {
          this._assistantsForChat$.next({});
        }
      }
    );
  }

  async getWorkspaceAssistantData(): Promise<AssistantChatData> {
    const workspace = await firstValueFrom(this.workspaceService.current$);
    const defaultAssistant = await firstValueFrom(this.experiencesInvokerService.defaultAssistant$);
    const defaultAssistantSettings = defaultAssistant?.settings as Experiences.ExperienceGeneralSettings;
    const defaultAssistantForChat = this.createAssistantChatData(defaultAssistant);

    const assistantTitle = getAssistantTitle(defaultAssistant);
    const isDefaultSearchExperience = assistantTitle === DEFAULT_SEARCH_EXPERIENCE_TITLE;
    const name = !assistantTitle || isDefaultSearchExperience ? workspace?.name : assistantTitle;
    const description = getAssistantDescription(defaultAssistant) || this.content.unleashAiDefaultDescription;

    return {
      experienceType: defaultAssistantForChat?.experienceType,
      id: undefined,
      unleashAiId: defaultAssistantForChat?.id,
      name,
      description,
      icon: { type: 'img', value: this.workspaceService.getLogo() },
      iconBlobId: defaultAssistantSettings?.iconBlobId,
      emoji: defaultAssistantSettings?.emoji,
      isDefault: true,
      state: 'enabled',
      modifiedAt: defaultAssistantForChat?.modifiedAt,
      conversationStarterMessages: defaultAssistantForChat?.conversationStarterMessages,
      pinToChatPage: defaultAssistantForChat?.pinToChatPage,
      fullAccess: this.isOwnerOrAdmin(),
    };
  }

  getCreatedByInfo(assistant: Experiences.ExperienceItem): { avatar: AvatarItemModel; description: string } {
    const avatar = this.avatarListService.getAvatarById(assistant.createdBy, 'id');
    return {
      avatar,
      description: 'By',
    };
  }

  async goToChatPageWithHistory(answerItem: AnswerSearchItem, isGlobalAssistant?: boolean) {
    const { query, assistantId } = answerItem;
    const timestamp = Date.now();
    const answer: Chats.AssistantMessage = this.createMessageFromAnswer(answerItem, timestamp);
    const currentFilters: Filters.Values = this.filtersService.allFilters;
    const filtersToQuestion: Filters.Values = await this.filtersService.transformFiltersForDisplay(currentFilters);
    const question = {
      content: query,
      filters: filtersToQuestion,
      timestamp,
    };
    const chatId = generateId();
    const historyItem: Chats.ChatHistoryItem = {
      chatId,
      userMessage: question,
      assistantMessage: answer,
    };
    this.createSession(assistantId, chatId, Experiences.KnowledgeType.Internal, 'general').then(() => {
      this.createHistoryMessage(historyItem, true);
    });
    const tempChatSession: Chats.Chat = { id: chatId, assistantId, chatHistory: [historyItem] };
    const isLauncher = await this.hubService.getIsLauncher();
    if (isLauncher) {
      localStorage.setItem(this.TEMP_CHAT_SESSION_STORAGE_KEY, JSON.stringify(tempChatSession));
    } else {
      this.localTempChat = tempChatSession;
    }
    this.chatResourcesService.updateResourceInCache(answerItem.resources);
    this.openChat(assistantId, null, null, isGlobalAssistant, chatId);
  }

  async openChat(
    assistantId?: string,
    query?: string,
    ignoreFilters: boolean = true,
    isGlobalAssistant?: boolean,
    chatId?: string,
    state = {}
  ) {
    let chatUrl = `/${CHAT_PAGE_PATH}`;
    const queryParams: string[] = [];
    if (this.isExtension) {
      chatUrl = `/${SIDE_PANEL_PAGE_PATH}${chatUrl}`;
    }

    if (chatId) {
      if (assistantId && assistantId !== UNLEASH_AI_CHAT_PATH) {
        chatUrl += `/${assistantId}`;
      }
      chatUrl += `/${CHAT_PAGE_PATH_SESSION}/${chatId}`;
    } else {
      chatUrl += `/${assistantId || UNLEASH_AI_CHAT_PATH}`;
    }
    if (query) {
      queryParams.push(`${QUESTION_DRAFT_QUERY}=${query}`);
    }
    //HACK: Add a flag to the URL to indicate that the global assistant is in use.
    if (isGlobalAssistant) {
      queryParams.push(`${IS_GLOBAL_ASSISTANT}=${true}`);
    }
    const currentFilters = this.filtersService.allFilters;
    if (!ignoreFilters && !isEmpty(currentFilters)) {
      const filtersUrl = this.filtersService.getFiltersAsUrlParams(currentFilters);
      queryParams.push(filtersUrl);
    }

    if (queryParams.length > 0) {
      chatUrl += `?${queryParams.join('&')}`;
    }

    const isLauncher = await this.hubService.getIsLauncher();
    if (this.isEmbed) {
      if (isLauncher) {
        return this.hubService.openStandardEmbed(chatUrl, true);
      }
      return this.routerService.navigateByUrl(chatUrl, { replaceUrl: true, state });
    }
    if (isLauncher) {
      return this.windowService.switchToStandard(chatUrl);
    }
    return this.routerService.navigateByUrl(chatUrl, { state });
  }

  navigateToChatByAssistantId(assistantId: string) {
    let url = [CHAT_PAGE_PATH, assistantId || UNLEASH_AI_CHAT_PATH].join('/');
    if (this.isExtension) {
      url = `${SIDE_PANEL_PAGE_PATH}/${url}`;
    }
    this.routerService.navigateByUrl(`/${url}`);
  }

  async createSession(
    assistantId: string,
    chatId: string,
    assistantKnowledgeType: Experiences.KnowledgeType,
    experienceType: Experiences.ExperienceType,
    ignoreChat?: boolean
  ): Promise<void> {
    try {
      this.chatSessionsPromise[chatId] = new ManualPromise();
      await this.service.createSession({ assistantId, newChatId: chatId, ignore: ignoreChat, assistantKnowledgeType, experienceType });
    } catch (error) {
      this.logger.error('Failed to create chat session', error);
    } finally {
      if (!this.chatSessionsPromise[chatId].status) {
        this.chatSessionsPromise[chatId].resolve(true);
      }
    }
  }

  async createHistoryMessage(historyItem: Chats.ChatHistoryItem, isGenerateName?: boolean, totalFilesCount?: number) {
    await this.getChatSessionPromise(historyItem.chatId);
    this.service.createHistoryItem({ history: historyItem, updateAutomaticName: isGenerateName, totalFilesCount });
  }

  getChatSessionPromise(chatId: string): ManualPromise<boolean> {
    return this.chatSessionsPromise[chatId];
  }

  convertToAnswerResources(resources: Search.ResultResourceItem[]): Chats.MessageResource[] {
    return (resources || []).map((r) => this.convertToAnswerResource(r));
  }

  convertToAnswerResource(resource: Search.ResultResourceItem): Chats.MessageResource {
    return {
      appId: resource.resource?.appId,
      externalId: resource.resource?.externalId,
      resourceId: resource.resource?.id,
      verificationStatus: resource.resource?.data?.verificationStatus,
      snippet: resource.snippet,
      id: resource.id,
      type: 'resource',
    };
  }

  async getValidAssistant(assistantId: string, cache: Experiences.CacheType): Promise<AssistantDetails> {
    if (!assistantId) {
      return;
    }
    try {
      const foundAssistant = await firstValueFrom(this.experiencesInvokerService.getExperience$(assistantId, cache));
      if (foundAssistant) {
        const validDetails = this.chatAssistantsService.isValidAssistantForChat(foundAssistant, true);
        return { assistant: foundAssistant, error: isBoolean(validDetails) ? null : validDetails };
      }
      return { error: 'no-access' };
    } catch (error) {
      if (error.status === 404) {
        return { error: 'not-found' };
      }
      if (error.status === 403) {
        return { error: 'no-access' };
      }
    }
  }

  getChatHistory$(chatId: string, pageToken: string): Observable<Chats.GetChatHistoryResponse> {
    if (this.tempChatSession?.id === chatId) {
      const { chatHistory } = this.tempChatSession;
      this.tempChatSession = null;
      return of({ items: chatHistory });
    }
    return this.service.getChatHistory$({ chatId, pageToken });
  }

  getTempChatSession(assistantId?: string) {
    if (this.localTempChat && this.localTempChat.assistantId == assistantId) {
      this.tempChatSession = this.localTempChat;
      const localTempChat = this.localTempChat;
      this.localTempChat = null;
      return localTempChat;
    }
    const tempChatFromStorage = this.extractSessionFromStorage();
    if (!tempChatFromStorage || tempChatFromStorage.assistantId != assistantId) {
      return;
    }
    localStorage.removeItem(this.TEMP_CHAT_SESSION_STORAGE_KEY);
    this.tempChatSession = tempChatFromStorage;
    return tempChatFromStorage;
  }

  private extractSessionFromStorage() {
    try {
      const storage = localStorage.getItem(this.TEMP_CHAT_SESSION_STORAGE_KEY);
      if (!storage) {
        return;
      }
      return JSON.parse(storage) as Chats.Chat;
    } catch (error) {
      return;
    }
  }

  private createMessageFromAnswer(answerItem: AnswerSearchItem, timestamp: number): Chats.AssistantMessage {
    const { state, text, searchId, resources, intent, searchParameters } = answerItem;
    if (!isEmpty(searchParameters)) {
      delete searchParameters.backendSelfQuery;
    }
    if (state === 'NoResults') {
      return { state: Chats.AssistantMessageState.NoResultsFound, timestamp, intent, searchParameters };
    }
    const updatedResources = this.convertToAnswerResources(resources);
    const answer: Chats.AssistantMessage = {
      content: text,
      state: state === 'RephraseRequired' ? Chats.AssistantMessageState.RephraseRequired : Chats.AssistantMessageState.ResultsAvailable,
      resources: updatedResources,
      searchId,
      timestamp,
      references: answerItem.references,
      searchParameters,
    };
    if (intent) {
      answer.intent = intent;
    }
    return answer;
  }

  deleteSession(sessionId: string) {
    this.service.deleteSession(sessionId);
  }

  async updateSession(request: Chats.UpdateChatRequest) {
    return this.service.update(request);
  }

  updateAnswersResources(chatHistory: Chats.ChatHistoryItem[]) {
    const externalResources = (chatHistory || [])
      .map((h) => this.getInternalResourceIds(h) || [])
      .filter((r) => !!r)
      .flat();
    this.chatResourcesService.updateRemoteResources(externalResources);
    this.updateSourceListInHistory(chatHistory);
  }

  private getInternalResourceIds(chatHistoryItem: Chats.ChatHistoryItem) {
    if (chatHistoryItem?.assistantMessage?.resources?.length) {
      return chatHistoryItem.assistantMessage.resources.map((r) => r?.externalId);
    }
    if (chatHistoryItem?.assistantMessage?.sourcesList?.length) {
      return chatHistoryItem.assistantMessage.sourcesList.map((r) => (r as Chats.MessageResource)?.externalId)?.filter((id) => !!id);
    }
  }

  private updateSourceListInHistory(chatHistory: Chats.ChatHistoryItem[]) {
    (chatHistory || []).forEach((history) => {
      if (history?.assistantMessage?.sourcesList?.length) {
        this.answerResourceAgenticService.updateResourceInCache(history.assistantMessage.sourcesList);
      }
    });
  }

  handleLeftChat(
    chatId: string,
    assistantData: AssistantChatData,
    currentMessageState,
    chatHistoryData: Chats.ChatHistoryItem[],
    draftQuestion: Chats.UserMessage,
    isIgnoreChat: boolean,
    totalFilesCount?: number,
    isExternalChat?: boolean
  ): { newChatHistoryItem: Chats.ChatHistoryItem; isCancelledWithResult: boolean } {
    const isCancelledWithResult = ['interactiveTyping', 'staticTyping', 'loadingWithPreAnswer'].includes(currentMessageState);
    const newChatHistoryItem = isCancelledWithResult
      ? this.getQueryCancelledWithResult(chatHistoryData, draftQuestion, chatId, assistantData)
      : this.getQueryCancelled(draftQuestion, chatId, assistantData);

    if (newChatHistoryItem) {
      newChatHistoryItem.chatId = chatId;
      if (!isExternalChat) {
        delete newChatHistoryItem.assistantMessage.resourceIds;
      }

      this.createHistoryMessage(
        newChatHistoryItem,
        isIgnoreChat || (isCancelledWithResult && chatHistoryData?.length === 1),
        totalFilesCount
      );
    }
    return { isCancelledWithResult, newChatHistoryItem };
  }

  private getQueryCancelledWithResult(
    chatHistoryData: Chats.ChatHistoryItem[],
    draftQuestion: Chats.UserMessage,
    chatId: string,
    assistantData: AssistantChatData
  ): Chats.ChatHistoryItem {
    if (!chatHistoryData || chatHistoryData.length === 0) {
      return this.getQueryCancelled(draftQuestion, chatId, assistantData);
    }
    const lastIndex = chatHistoryData.length - 1;
    const lastHistoryItem = chatHistoryData[lastIndex];

    if (lastHistoryItem?.assistantMessage) {
      if (isAgenticAssistant(assistantData) && !lastHistoryItem?.assistantMessage?.content) {
        lastHistoryItem.assistantMessage.state = Chats.AssistantMessageState.QueryCancelled;
      } else {
        lastHistoryItem.assistantMessage.state = Chats.AssistantMessageState.QueryCancelledWithResult;
      }
    }
    return lastHistoryItem;
  }

  private getQueryCancelled(draftQuestion: Chats.UserMessage, chatId: string, assistantData: AssistantChatData): Chats.ChatHistoryItem {
    const cancelHistoryItem: Chats.ChatHistoryItem = {
      userMessage: {
        content: draftQuestion.content,
        filters: draftQuestion.filters,
        timestamp: draftQuestion.timestamp,
        blobIds: draftQuestion.blobIds,
        action: draftQuestion.action,
      },
      assistantMessage: { state: Chats.AssistantMessageState.QueryCancelled, timestamp: Date.now() },
      chatId,
      modelId: assistantData?.model,
    };
    return cancelHistoryItem;
  }

  createNewSession(
    assistantId: string,
    assistantKnowledgeType: Experiences.KnowledgeType,
    experienceType: Experiences.ExperienceType,
    ignoreChat?: boolean,
    newChatId?: string
  ): string {
    const chatId = newChatId ?? generateId();
    this.createSession(assistantId, chatId, assistantKnowledgeType, experienceType, ignoreChat);
    return chatId;
  }
}
